/**
 * The director's extra visit, refused bookings shown, and the Extra support list (Eli, 20 Oct 2026; D-1020-EXTRA).
 *
 * The Queue 1 processor has no exception path (`AuthorizationExceptionRequested` is always false), so a booking past a
 * tenant's monthly limit is refused `Authorization Exhausted`. Two things follow, both here and both without touching
 * the processor:
 *   - the refusal is shown to a person and stays on Needs your attention until someone presses Done;
 *   - the director can write an extra visit directly (the staff-unit path of D-1.1.0-10), and the "Extra support" list
 *     — tenants who may be offered open spots, up to {@link EXTRA_SUPPORT_CAP} visits a month in all — is the director's
 *     standing OK to do the same for those tenants, from the open spots a cancellation leaves.
 *
 * No dependencies, so the app and the API both use it.
 */
import type { Role } from './roles';

/** The app's own file: tenantId → who switched Extra support on or off, and when. Never a SharePoint column. */
export const EXTRA_SUPPORT_FILE = '_app/extra-support.json';
/** The refusals someone pressed Done on: transaction id → who and when. No deletes: a pressed one stays in the file. */
export const NOT_BOOKED_DONE_FILE = '_app/not-booked-done.json';
/** Visits a month in all (their usual ones included) for a tenant on the Extra support list. */
export const EXTRA_SUPPORT_CAP = 4;
/** A refusal shows on Needs your attention this many days, until someone presses Done. */
export const NOT_BOOKED_DAYS = 14;
/** The reason an Extra support booking carries in the record. */
export const EXTRA_SUPPORT_REASON = 'Extra support';
/** The audit events (EventCategory Administrative Override — api/src/admin/audit.ts). */
export const EXTRA_AUDIT = {
  visit: 'EXTRA_VISIT_BOOKED',
  support: 'EXTRA_SUPPORT_BOOKED',
  moved: 'EXTRA_VISIT_MOVED',
  setOn: 'EXTRA_SUPPORT_ON',
  setOff: 'EXTRA_SUPPORT_OFF',
  done: 'EXTRA_NOT_BOOKED_DONE',
} as const;

export interface ExtraSupportEntry {
  on: boolean;
  setBy: string;
  setAt: string;
}
export type ExtraSupportFile = Record<string, ExtraSupportEntry>;
export interface NotBookedDone {
  by: string;
  at: string;
}
export type NotBookedDoneFile = Record<string, NotBookedDone>;

export const isExtraSupportOn = (file: ExtraSupportFile | null | undefined, tenantId: string): boolean => file?.[tenantId]?.on === true;
export const extraSupportIds = (file: ExtraSupportFile | null | undefined): string[] => Object.entries(file ?? {}).filter(([, e]) => e.on === true).map(([id]) => id).sort();

/** The free override (any visits, any month): the administrator's alone. */
export const canExtraVisit = (role: Role | string | null | undefined): boolean => role === 'admin';
/** Book this spot for a tenant on the list: the director's standing OK is the checkbox, so the three booking roles. */
export const canBookExtraSupport = (role: Role | string | null | undefined): boolean => role === 'admin' || role === 'coadmin' || role === 'scheduler';
/** The checkbox on a tenant's Housekeeping section. */
export const canSetExtraSupport = (role: Role | string | null | undefined): boolean => role === 'admin' || role === 'coadmin';

/** The notes an extra visit's row starts with (api/src/housekeeping/extra.ts) — how a move knows whose OK it carries. */
export const EXTRA_NOTE_VISIT = 'Extra visit (the director’s OK)';
export const EXTRA_NOTE_SUPPORT = 'Extra support (the director’s standing OK)';

/** Visits that count as "a visit that week" for the Extra support block: anything live (a Proposed line included). */
export const NOT_LIVE_STATUSES: ReadonlySet<string> = new Set(['Cancelled', 'Rescheduled', 'No Show', 'Unable to Complete', 'Superseded', 'Voided']);
/** Does a tenant already have a live visit in the Monday-to-Friday week of this day? (`visits` carry yyyyMMdd keys.) */
export function hasVisitThatWeek(visits: readonly { status: string; dayKey: string }[], monday: string, friday: string): boolean {
  return visits.some((v) => v.dayKey >= monday && v.dayKey <= friday && !NOT_LIVE_STATUSES.has(v.status));
}

/** The refusal in words — one wording for Book this spot and for a Move (Eli, 20 Oct): an Extra support tenant already has a visit that week. */
export const weekBlockWords = (name: string): string => `${name} already has a visit that week, so this spot is not offered. The administrator can still add an extra visit with a reason.`;

/** Past the monthly limit: the preview knows the cycle's used / authorized counts (null authorized = no fixed limit). */
export function overMonthlyLimit(cycle: { authorized: number | null; used: number } | null | undefined): boolean {
  return !!cycle && cycle.authorized !== null && cycle.used >= cycle.authorized;
}

/** Statuses that are not a visit the tenant had (or has): called off, moved on, or not carried out. */
const NOT_A_VISIT = new Set(['Cancelled', 'Rescheduled', 'No Show', 'Unable to Complete', 'Superseded', 'Voided']);
/** The month a day falls in (yyyyMM): a housekeeping cycle is a calendar month (HK-yyyyMM). */
export const monthOfDay = (dayKey: string): string => dayKey.slice(0, 6);

/** Visits a tenant has in a month, extra ones included — Proposed lines count (they are planned for the month). */
export function monthVisitCount(visits: readonly { status: string; dayKey: string }[], month: string): number {
  return visits.filter((v) => monthOfDay(v.dayKey) === month && !NOT_A_VISIT.has(v.status)).length;
}

export const underExtraCap = (count: number): boolean => count < EXTRA_SUPPORT_CAP;

/** One tenant on the open-spot list. */
export interface SpotCandidate {
  tenantId: string;
  unit: string;
  name: string;
  /** Visits this month, extra ones included. */
  monthVisits: number;
  /** The most recent visit before the day (yyyyMMdd), or null when they have none. */
  lastVisitKey: string | null;
  /** For the Call button (never printed). */
  phone: string | null;
}

/** Fewest visits this month first, then longest since their last visit (no visit at all first), then the unit. */
export function orderSpotCandidates<T extends Pick<SpotCandidate, 'monthVisits' | 'lastVisitKey' | 'unit'>>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => {
    if (a.monthVisits !== b.monthVisits) return a.monthVisits - b.monthVisits;
    const la = a.lastVisitKey ?? '';
    const lb = b.lastVisitKey ?? '';
    if (la !== lb) return la < lb ? -1 : 1; // an older (smaller) key, or none (''), is longer since
    return a.unit.localeCompare(b.unit, undefined, { numeric: true });
  });
}

/** A day with at least one open position, and who could be offered it. */
export interface OpenSpotDay {
  dayKey: string;
  /** Free positions within the day's planned capacity. */
  free: number;
  /** The first free position (the one Book this spot takes). */
  firstFree: number;
  candidates: SpotCandidate[];
}
export interface ExtraSpotsResponse {
  days: OpenSpotDay[];
}

/** POST /api/housekeeping/extra */
export interface ExtraBookRequest {
  /** `director`: the administrator's extra visit with a reason; `support`: Book this spot for an Extra support tenant. */
  kind: 'director' | 'support';
  tenantId: string;
  dayKey: string;
  position?: number | null;
  /** Absent: the first free position's own part of the day (an open-spot press names none). */
  period?: 'Morning' | 'Afternoon';
  /** Required for `director` (a short reason). `support` always carries "Extra support". */
  reason?: string;
  note?: string;
  deepClean?: boolean;
  lengthMinutes?: number | null;
  /** Absent: the EMAIL_ON_PHONE_BOOKING setting's answer (as the Book sheet starts). */
  emailPleaseConfirm?: boolean;
  startMinutes?: number | null;
  onOverlap?: 'push' | 'keep' | 'swap';
}

export interface ExtraSupportListResponse {
  /** Tenant ids on the list. */
  on: string[];
}

/** The refusals' kinds of words (a Queue 1 refusal that an Extra visit or a second try can answer). */
export const AUTH_EXHAUSTED = 'Authorization Exhausted';
/** The processor's word for a second visit in the tenant's Monday-to-Friday week (an Extra support tenant: the administrator may still add an extra visit). */
export const WEEKLY_CONFLICT = 'Weekly Conflict';

/** The Queue 1 outcomes that are not a refusal. */
export const isRefusedOutcome = (processingStatus: string, outcomeCode: string | null | undefined): boolean => {
  if (processingStatus === 'Rejected' || processingStatus === 'Failed') return outcomeCode !== 'Duplicate Ignored';
  return processingStatus === 'Completed' && !!outcomeCode && outcomeCode !== 'Applied' && outcomeCode !== 'Duplicate Ignored';
};

/** Operations whose refusal reads "Not booked" (a visit was asked for); any other reads "Not done". */
export const BOOKING_OPERATIONS: ReadonlySet<string> = new Set(['Create Appointment', 'Reschedule Appointment']);
